import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Highlighter, Languages, Search } from "lucide-react";

function paraOf(node) {
  const el = node?.nodeType === 3 ? node.parentElement : node;
  return el?.closest?.("[data-para]") || null;
}

export default function SelectionMenu({ containerRef, onHighlight, onTranslate, onSearch, dark = false }) {
  const [sel, setSel] = useState(null);

  useEffect(() => {
    const read = () => {
      const s = window.getSelection();
      const text = s?.toString().trim() || "";
      const root = containerRef.current;
      if (!s || s.rangeCount === 0 || !text || text.length > 1000 || !root || !root.contains(s.anchorNode)) { setSel(null); return; }
      const rect = s.getRangeAt(0).getBoundingClientRect();
      const pa = paraOf(s.anchorNode);
      const pb = paraOf(s.focusNode);
      const para = pa && pa === pb ? Number(pa.dataset.para) : null;
      setSel({ text, para, x: rect.left + rect.width / 2, y: rect.top });
    };
    const onUp = () => { setTimeout(read, 10); };
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchend", onUp);
    document.addEventListener("keyup", onUp);
    return () => {
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchend", onUp);
      document.removeEventListener("keyup", onUp);
    };
  }, [containerRef]);

  const act = (fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const s = sel;
    setSel(null);
    window.getSelection()?.removeAllRanges();
    fn(s);
  };

  const left = sel ? Math.min(Math.max(sel.x, 140), window.innerWidth - 140) : 0;
  const top = sel ? Math.max(sel.y - 52, 8) : 0;
  const btn = "flex items-center gap-1.5 px-3 h-9 text-xs font-semibold whitespace-nowrap transition-colors";
  return (
    <AnimatePresence>
      {sel && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
          onMouseDown={(e) => e.preventDefault()} style={{ left, top }}
          className={`fixed z-[60] -translate-x-1/2 flex shadow-xl ${dark ? "bg-white text-[#0D1B3E]" : "bg-[#0D1B3E] text-white"}`} data-testid="selection-menu">
          {onHighlight && sel.para !== null && (
            <button onMouseDown={act(onHighlight)} className={`${btn} hover:bg-[#002FA7] hover:text-white`} data-testid="selection-highlight-btn"><Highlighter size={14} /> Highlight</button>
          )}
          <button onMouseDown={act(onTranslate)} className={`${btn} hover:bg-[#002FA7] hover:text-white`} data-testid="selection-translate-btn"><Languages size={14} /> Translate</button>
          <button onMouseDown={act(onSearch)} className={`${btn} hover:bg-[#002FA7] hover:text-white`} data-testid="selection-search-btn"><Search size={14} /> Search</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
